import { FC } from "react";
import { useNavigate, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/button";
import { api } from "@/modules/api";
import { Uuid } from "@/domains/global/identifiers";
import { atlasRoutes } from "@/app/router/routes";
import { Box, Card, Heading, Text, Flex } from "@radix-ui/themes";

export const CapturedWebsitesViewPage: FC = () => {
  const { capturedWebsiteId } = useParams<{ capturedWebsiteId: Uuid }>();
  const navigate = useNavigate();

  if (!capturedWebsiteId) return <div>Invalid ID</div>;

  const result = useQuery({
    queryKey: ["captured-website", capturedWebsiteId],
    queryFn: () =>
      api.get("/internal/v1/captured-websites/{captured_website_id}", {
        params: { path: { captured_website_id: capturedWebsiteId } },
      }),
  });

  const handleDelete = async () => {
    await api.delete("/internal/v1/captured-websites/{captured_website_id}", {
      params: { path: { captured_website_id: capturedWebsiteId } },
    });
    navigate(atlasRoutes.capturedWebsites({}).path);
  };

  if (result.isLoading) return <div>Loading...</div>;
  if (result.error) return <div>Error loading captured website</div>;
  if (!result.data?.data) return <div>Captured website not found</div>;

  const website = result.data.data;

  return (
    <Box p="4">
      <Heading size="6" mb="4">
        View Captured Website
      </Heading>

      <Card mb="4">
        <Flex direction="column" gap="3">
          <Box>
            <Text size="2" color="gray">
              Website ID:
            </Text>
            <Text
              size="3"
              weight="bold"
              style={{
                fontFamily: "monospace",
                userSelect: "all",
                paddingLeft: "8px",
                fontStyle: "italic",
              }}
            >
              {website.id}
            </Text>
          </Box>
          <Box>
            <Text size="2" color="gray">
              Title:
            </Text>
            <Text size="3" weight="bold">
              {" "}
              {website.website_title ?? "Untitled"}
            </Text>
          </Box>
          <Box>
            <Text size="2" color="gray">
              URL:
            </Text>
            <Text size="3"> {website.website_url ?? "No URL"}</Text>
          </Box>
          <Box>
            <Text size="2" color="gray">
              Captured At:
            </Text>
            <Text size="3">
              {" "}
              {website.captured_at ? new Date(website.captured_at).toLocaleString() : "Unknown"}
            </Text>
          </Box>
          <Box>
            <Text size="2" color="gray">
              Created At:
            </Text>
            <Text size="3"> {new Date(website.created_at).toLocaleString()}</Text>
          </Box>
          {website.uploaded_file_id && (
            <Box>
              <Text size="2" color="gray">
                Uploaded File ID:
              </Text>
              <Text size="3" style={{ fontFamily: "monospace" }}>
                {" "}
                {website.uploaded_file_id}
              </Text>
            </Box>
          )}
        </Flex>
      </Card>

      <Flex gap="3" mb="4">
        {website.website_url && (
          <Button size="2" variant="soft" asChild>
            <a href={website.website_url} target="_blank" rel="noopener noreferrer">
              Open Website URL
            </a>
          </Button>
        )}
        {website.uploaded_file_id && (
          <Button
            size="2"
            variant="soft"
            onClick={() =>
              navigate(
                atlasRoutes.uploadedFilesView({
                  params: { uploadedFileId: website.uploaded_file_id ?? "unknown" },
                }).path
              )
            }
          >
            View Uploaded File
          </Button>
        )}
        <Button size="2" color="red" onClick={handleDelete}>
          Delete
        </Button>
      </Flex>

      <details>
        <summary style={{ cursor: "pointer", marginBottom: "8px" }}>
          <Text size="2" color="gray">
            Raw JSON Data
          </Text>
        </summary>
        <pre
          style={{
            fontSize: "12px",
            background: "var(--gray-a3)",
            padding: "12px",
            borderRadius: "4px",
            overflow: "auto",
          }}
        >
          {JSON.stringify(website, null, 2)}
        </pre>
      </details>
    </Box>
  );
};
